import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Button, Link } from '@mui/material';
import BigOnOffCards from '../components/BigOnOffCards';
import MessagePreview from '../components/MessagePreview';
import AdvancedSection from '../components/AdvancedSection';
import SmallToggleRow from '../components/SmallToggleRow';

const VARIABLES = ['{{first_name}}', '{{practice_name}}', '{{next_open_time}}', '{{practice_phone}}'];

// Read-only summary view (shown when Practice Details owns out-of-hours settings)
function OutOfHoursSummary({ formData, onEditHere }) {
    const navigate = useNavigate();
    const isEnabled = formData.out_of_hours_enabled ?? true;

    return (
        <Box>
            <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#00A76F', mb: '10px',
            }}>
                Out of hours
            </Typography>
            <Typography sx={{
                fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
            }}>
                Auto-reply when closed
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#7A7A7A', lineHeight: 1.6, mb: '24px' }}>
                These settings are managed in Practice Details.
            </Typography>

            {/* Status badge */}
            <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Status:
                </Typography>
                <Chip
                    label={isEnabled ? 'Enabled' : 'Disabled'}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        bgcolor: isEnabled ? 'rgba(0, 167, 111, 0.12)' : 'rgba(145, 158, 171, 0.12)',
                        color: isEnabled ? '#007B55' : '#637381',
                        borderRadius: '6px',
                    }}
                />
            </Box>

            {/* Message preview (truncated) */}
            {isEnabled && formData.out_of_hours_message && (
                <Box
                    sx={{
                        p: 2,
                        mb: 2.5,
                        borderRadius: '10px',
                        bgcolor: 'rgba(145, 158, 171, 0.06)',
                        border: '1px solid rgba(145, 158, 171, 0.16)',
                    }}
                >
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                        Out-of-hours message
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#374151',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.6,
                        }}
                    >
                        {formData.out_of_hours_message}
                    </Typography>
                </Box>
            )}

            {/* Toggle status chips */}
            <Box display="flex" flexWrap="wrap" gap={1} mb={3}>
                <Chip
                    icon={<span style={{ fontSize: 14 }}>{(formData.weekend_same_message ?? true) ? '✓' : '✗'}</span>}
                    label="Weekends"
                    size="small"
                    variant="outlined"
                    sx={{
                        fontWeight: 500,
                        fontSize: 12,
                        borderRadius: '6px',
                        borderColor: (formData.weekend_same_message ?? true) ? '#00A76F' : 'rgba(145,158,171,0.32)',
                        color: (formData.weekend_same_message ?? true) ? '#007B55' : '#637381',
                    }}
                />
                <Chip
                    icon={<span style={{ fontSize: 14 }}>{(formData.bank_holiday_detect ?? true) ? '✓' : '✗'}</span>}
                    label="Bank Holidays"
                    size="small"
                    variant="outlined"
                    sx={{
                        fontWeight: 500,
                        fontSize: 12,
                        borderRadius: '6px',
                        borderColor: (formData.bank_holiday_detect ?? true) ? '#00A76F' : 'rgba(145,158,171,0.32)',
                        color: (formData.bank_holiday_detect ?? true) ? '#007B55' : '#637381',
                    }}
                />
            </Box>

            {/* Link to Practice Details */}
            <Box
                sx={{
                    p: 2,
                    borderRadius: '10px',
                    bgcolor: 'rgba(59, 130, 246, 0.06)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    mb: 2,
                }}
            >
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                    To change these settings, go to:
                </Typography>
                <Link
                    component="button"
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate('/dashboard/user/practice')}
                >
                    Practice Details → Opening Hours → Out-of-Hours
                </Link>
            </Box>

            {/* Fallback edit toggle */}
            <Button
                size="small"
                variant="text"
                onClick={onEditHere}
                sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    color: '#637381',
                    p: 0,
                    '&:hover': { color: '#374151', bgcolor: 'transparent' },
                }}
            >
                Edit here instead →
            </Button>
        </Box>
    );
}

OutOfHoursSummary.propTypes = {
    formData: PropTypes.object.isRequired,
    onEditHere: PropTypes.func.isRequired,
};

// Full editing form (shown when user clicks "Edit here instead")
function OutOfHoursForm({ formData, onChange, practiceName, practiceLogo, practicePhone }) {
    const sampleValues = {
        first_name: 'Sarah',
        practice_name: practiceName || 'Your Practice',
        next_open_time: '9:00 AM Monday',
        practice_phone: practicePhone || '0123 456 7890',
    };

    return (
        <Box>
            <Typography sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#00A76F', mb: '10px',
            }}>
                Out of hours
            </Typography>
            <Typography sx={{
                fontSize: 22, fontWeight: 800, color: '#0D0D0D',
                lineHeight: 1.3, letterSpacing: '-0.4px', mb: 1,
            }}>
                Auto-reply when closed?
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#7A7A7A', lineHeight: 1.6, mb: '28px' }}>
                Send an automatic response to patients who message after hours.
            </Typography>

            <BigOnOffCards
                checked={formData.out_of_hours_enabled ?? true}
                onChange={(val) => onChange('out_of_hours_enabled', val)}
                yesTitle="Yes, auto-reply"
                yesDesc="Keep patients informed"
                noTitle="No, I'll do it"
                noDesc="Team handles manually"
            />

            {formData.out_of_hours_enabled && (
                <MessagePreview
                    message={formData.out_of_hours_message}
                    onChange={(val) => onChange('out_of_hours_message', val)}
                    variables={VARIABLES}
                    sampleValues={sampleValues}
                    practiceName={practiceName}
                    practiceLogo={practiceLogo}
                />
            )}

            <AdvancedSection>
                <SmallToggleRow
                    checked={formData.weekend_same_message ?? true}
                    onChange={(val) => onChange('weekend_same_message', val)}
                    label="Apply on weekends"
                    desc="Uses the same out-of-hours message on Saturdays and Sundays."
                />
                <SmallToggleRow
                    checked={formData.bank_holiday_detect ?? true}
                    onChange={(val) => onChange('bank_holiday_detect', val)}
                    label="Detect UK bank holidays"
                    desc="WhatsApp AI recognises bank holidays and adjusts the message automatically."
                />
            </AdvancedSection>
        </Box>
    );
}

OutOfHoursForm.propTypes = {
    formData: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    practiceName: PropTypes.string,
    practiceLogo: PropTypes.string,
    practicePhone: PropTypes.string,
};

OutOfHoursForm.defaultProps = {
    practiceName: '',
    practiceLogo: '',
    practicePhone: '',
};

// Main export — shows read-only summary, with fallback to full form
export default function OutOfHoursStep({ formData, onChange, practiceName, practiceLogo, practicePhone }) {
    // Practice Details always owns out-of-hours settings
    const practiceDetailsOwnsSettings = true;
    const [showFullForm, setShowFullForm] = useState(false);

    if (practiceDetailsOwnsSettings && !showFullForm) {
        return (
            <OutOfHoursSummary
                formData={formData}
                onEditHere={() => setShowFullForm(true)}
            />
        );
    }

    return (
        <OutOfHoursForm
            formData={formData}
            onChange={onChange}
            practiceName={practiceName}
            practiceLogo={practiceLogo}
            practicePhone={practicePhone}
        />
    );
}

OutOfHoursStep.propTypes = {
    formData: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    practiceName: PropTypes.string,
    practiceLogo: PropTypes.string,
    practicePhone: PropTypes.string,
};

OutOfHoursStep.defaultProps = {
    practiceName: '',
    practiceLogo: '',
    practicePhone: '',
};
